import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { useNavigate } from "react-router-dom";

import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import LeadList from "./LeadList";
import MailingListData from "./MailingListData";
import { PAGES_DOMAIN_URL } from "constants";

export default ({ listId }) => {
  const navigate = useNavigate();

  const [loadingNotificationsToggle, setLoadingNotificationsToggle] =
    useState(false);
  const [responderModal, setResponderModal] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [unsetResponderLoading, setUnsetResponderLoading] = useState(null);

  // Fetch data
  const GET_MAILING_LIST_DATA = gql`
    query GetMailingListData($listId: ID!) {
      mailingListData(listId: $listId) {
        list {
          id
          name
          pages {
            id
            name
            code
          }
          lead_notification
          responder {
            id
            name
          }
        }
        last24HoursLeads
        last24HoursVerifiedLeads
        prev24HoursLeads
        prev24HoursVerifiedLeads
        last7DaysLeads
        last7DaysVerifiedLeads
        prev7DaysLeads
        prev7DaysVerifiedLeads
        allLeads
        allVerifiedLeads
      }
    }
  `;

  const { data, error, refetch } = useQuery(GET_MAILING_LIST_DATA, {
    variables: {
      listId,
    },
    skip: !listId,
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("error fetching mailing list data", error);
    captureException(error);
  }

  const onPageClick = (code) => {
    window.open(`${PAGES_DOMAIN_URL}/?code=${code}`, "_blank").focus();
  };

  useEffect(() => {
    if (data) {
      if (!data.mailingListData.list) navigate("/mailing-lists");
    }
  }, [data]);

  // handle Notifications toggle
  const TOGGLE_PAGE_NOTIFICATIONS = gql`
    mutation TogglePageNotifications($listId: ID!) {
      togglePageListNotifications(listId: $listId) {
        id
        lead_notification
      }
    }
  `;

  const [togglePageListNotifications] = useMutation(TOGGLE_PAGE_NOTIFICATIONS);

  const onNotificationsToggleClick = async () => {
    try {
      if (!data) return;
      if (loadingNotificationsToggle) return;

      setLoadingNotificationsToggle(true);

      await togglePageListNotifications({
        variables: {
          listId: data.mailingListData.list.id,
        },
      });

      setLoadingNotificationsToggle(false);
    } catch (error) {
      console.log("error onNotificationsToggleClick", error);
      captureException(error);
      setLoadingNotificationsToggle(false);
      return;
    }
  };

  // handle Autoresponder selection
  const onSetAutoresponerClick = () => {
    setResponderModal(true);
  };

  const closeResponderModal = () => {
    setResponderModal(false);
  };

  const SET_RESPONDER = gql`
    mutation SetResponder($listId: ID!, $responderId: ID) {
      setResponder(listId: $listId, responderId: $responderId) {
        resultCode
      }
    }
  `;

  const [setResponder] = useMutation(SET_RESPONDER);

  const unsetAutoresponder = async () => {
    try {
      if (!data) return;
      if (unsetResponderLoading) return;

      setErrorMessage(null);
      setUnsetResponderLoading(true);

      const result = await setResponder({
        variables: {
          listId,
          responderId: null,
        },
      });

      const { resultCode } = result.data.setResponder;
      console.log("resultCode", resultCode);

      setUnsetResponderLoading(false);

      if (resultCode !== 0) {
        setErrorMessage(
          "An error occurred while submitting the request. Please try again or contact support."
        );

        return;
      }

      refetch();
      closeResponderModal();
    } catch (error) {
      console.log("error unsetAutoresponder", error);
      captureException(error);
      setUnsetResponderLoading(false);
      return;
    }
  };

  // handle List delete
  const onDeleteClick = () => {
    setConfirmDelete(true);
  };

  const closeDeleteConfirm = () => {
    setConfirmDelete(false);
  };

  const DELETE_MAILING_LIST = gql`
    mutation DeleteMailingList($listId: ID!) {
      deleteMailingList(listId: $listId) {
        resultCode
      }
    }
  `;

  const [deleteMailingList] = useMutation(DELETE_MAILING_LIST);

  const onConfirmDelete = async () => {
    try {
      if (!confirmDelete) return;

      if (loadingDelete) return;

      setLoadingDelete(true);

      const result = await deleteMailingList({
        variables: {
          listId,
        },
      });

      setLoadingDelete(false);

      const { resultCode } = result.data.deleteMailingList;

      console.log({ resultCode });

      if (resultCode === 6) {
        setErrorMessage(
          "The mailing list is connected to a landing page. Change the mailing list in the page settings to be able to delete this list."
        );

        setConfirmDelete(null);
        return;
      }

      if (resultCode !== 0) {
        setErrorMessage(
          "An error occurred while submitting the request. Please try again or contact support."
        );

        setConfirmDelete(null);
        return;
      }

      refetch();

      navigate("/mailing-lists");
    } catch (error) {
      console.log("onConfirmDelete error", error);
      captureException(error);
      setLoadingDelete(false);
      return;
    }
  };

  // handle Error message
  const closeErrorMessage = () => {
    setErrorMessage(null);
  };

  console.log("data", data);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="280px" />
        </LoadingBox>
      )}
      {data?.mailingListData.list && (
        <MailingListData
          data={data}
          onPageClick={onPageClick}
          onNotificationsToggleClick={onNotificationsToggleClick}
          onSetAutoresponerClick={onSetAutoresponerClick}
          closeResponderModal={closeResponderModal}
          responderModal={responderModal}
          refetch={refetch}
          onDeleteClick={onDeleteClick}
          closeDeleteConfirm={closeDeleteConfirm}
          confirmDelete={confirmDelete}
          onConfirmDelete={onConfirmDelete}
          loadingDelete={loadingDelete}
          errorMessage={errorMessage}
          closeErrorMessage={closeErrorMessage}
          unsetAutoresponder={unsetAutoresponder}
        />
      )}
    </>
  );
};
